// Temporary workarounds for data the backend doesn't serve the right way yet.
// Everything here is search/business logic that belongs on the server; each
// block names its task in docs/BACKEND_TASKS.md and is switched off by the
// matching flag in src/config/backendFeatures.ts. Delete a block once its
// flag has been on in production.

import { listingPromoRank } from "../../helpers/constants/listingBadges";
import { isHiddenListing, normalizeListing } from "../../helpers/listingNormalize";

export type Page = { list?: any[]; total?: number; meta?: Record<string, any> } & Record<string, any>;
type Fetch = (filters: Record<string, any>) => Promise<Page>;

/**
 * B6 — hidden test listing (and B1: CHUI+bishkek shown as Bishkek). Filtering after the
 * response skews `total`; the server should just not return such rows.
 */
export function cleanPage(data: Page): Page {
  if (!data || !Array.isArray(data.list)) return data;
  const list = data.list.filter((p) => !isHiddenListing(p)).map(normalizeListing);
  const removed = data.list.length - list.length;
  return { ...data, list, total: Math.max(0, Number(data.total ?? list.length) - removed) };
}

/**
 * B1 — the two merged Bishkek requests lose the server's HOT → VIP → TOP
 * order; restore it for the merged page. Price sorting is left untouched.
 */
export function promotedFirst(data: Page): Page {
  if (!data || !Array.isArray(data.list)) return data;
  const list = data.list
    .map((p, i) => ({ p, i, r: listingPromoRank(p) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.p);
  return { ...data, list };
}

const sortLike = (filters: Record<string, any>) => (a: any, b: any) => {
  if (filters.sortBy === "price") {
    const d = Number(a.price) - Number(b.price);
    return String(filters.sortOrder).toUpperCase() === "DESC" ? -d : d;
  }
  return Date.parse(b.createdat) - Date.parse(a.createdat);
};

/**
 * B1 — until the region migration runs, Bishkek lives in two shapes: new
 * listings as region=BISHKEK, older ones as region=CHUI + district=bishkek.
 * Two requests merged and paginated in the browser.
 */
export async function getBishkekMerged(fetch: Fetch, filters: Record<string, any>): Promise<Page> {
  const page = Math.max(1, Number(filters.page) || 1);
  const size = Math.max(1, Number(filters.offset) || 10);
  const upTo = { ...filters, page: 1, offset: page * size };
  const [a, b] = await Promise.all([
    fetch({ ...upTo, region: "BISHKEK" }),
    fetch({ ...upTo, region: "CHUI", district: "bishkek" }),
  ]);
  const seen = new Set<number>();
  const all = [...(a?.list ?? []), ...(b?.list ?? [])]
    .filter((p) => !seen.has(p.id) && seen.add(p.id))
    .sort(sortLike(filters));
  const total = Number(a?.total ?? 0) + Number(b?.total ?? 0);
  const list = all.slice((page - 1) * size, page * size);
  return cleanPage({ ...(b ?? a), list, total, meta: { ...(b?.meta ?? a?.meta), hasMore: page * size < total } });
}

/** B1 — «Чуйская область» without the old Bishkek rows. */
export async function getChuiWithoutBishkek(fetch: Fetch, filters: Record<string, any>): Promise<Page> {
  const page = Math.max(1, Number(filters.page) || 1);
  const size = Math.max(1, Number(filters.offset) || 10);
  const inBishkek = await fetch({ ...filters, region: "CHUI", district: "bishkek", page: 1, offset: 1 });
  const skip = Number(inBishkek?.total ?? 0);
  const data = await fetch({ ...filters, region: "CHUI", page: 1, offset: page * size + skip });
  const rest = (data?.list ?? []).filter((p: any) => p.district !== "bishkek");
  const total = Math.max(0, Number(data?.total ?? 0) - skip);
  return cleanPage({ ...data, list: rest.slice((page - 1) * size, page * size), total, meta: { ...data?.meta, hasMore: page * size < total } });
}

/**
 * B3 — the price filter compares in dollars, so a som range is converted at
 * the browser's rate. The server should take priceCurrency and convert with
 * its own rate (and sort by a normalised price column).
 */
export function priceToUsd(value: string | number | undefined, currency: "USD" | "KGS", usdKgs: number): number {
  const n = Number(value) || 0;
  if (!n) return 0;
  return currency === "KGS" ? Math.round(n / usdKgs) : n;
}
